import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useToast } from '../../hooks/use-toast';
import { API } from '../../helpers';
import { useQueryStore } from '../../store/queryStore';
import {
  pageVariants,
  ApiKeyQueryCard,
  SavedApiKeyCard,
  ServiceStatusCard,
  SupportedModelsCard
} from '../../components/Home/BentoCards';
import ResponsiveBentoGrid from '../../components/Home/ResponsiveBentoGrid';
import ErrorBoundary from '../../components/ErrorBoundary';

const Home = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // 使用稳定的选择器来避免Zustand错误，添加更严格的错误检查
  const apikey = useQueryStore(state => state?.apikey || '');
  const selectedService = useQueryStore(state => state?.selectedService || { name: '', url: '' });
  const savedKeys = useQueryStore(state => {
    if (!state || !Array.isArray(state.savedKeys)) {
      return [];
    }
    return state.savedKeys;
  });
  const savedApikey = useQueryStore(state => state?.savedApikey || '');
  const savedService = useQueryStore(state => state?.savedService || { name: '', url: '' });
  
  const setAPIKey = useQueryStore(state => state.setAPIKey);
  const setSelectedService = useQueryStore(state => state.setSelectedService);
  const performQuery = useQueryStore(state => state.performQuery);
  const performSavedQuery = useQueryStore(state => state.performSavedQuery);
  const performQueryWithKey = useQueryStore(state => state.performQueryWithKey);
  const clearAll = useQueryStore(state => state.clearAll);
  const hasQueryData = useQueryStore(state => state.hasQueryData);
  
  // 使用useMemo计算hasSaved状态，避免函数调用
  const hasSaved = useMemo(() => {
    return !!(savedApikey && savedService.url) || savedKeys.length > 0;
  }, [savedApikey, savedService.url, savedKeys.length]);
  
  const [loading, setLoading] = useState(false);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [baseUrls, setBaseUrls] = useState({});

  // 添加问候语 useMemo - 从Search页面复用
  const greetingText = useMemo(() => {
    const h = new Date().getHours();
    let greet;
    if (h >= 5 && h < 12) greet = '早上好';
    else if (h >= 12 && h < 14) greet = '中午好';
    else if (h >= 14 && h < 18) greet = '下午好';
    else greet = '晚上好';
    return `👋${greet}`;
  }, []);

  // 获取服务列表
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await API.get('/api/services');
        const services = response.data.services;
        const serviceMap = {};
        services.forEach(service => {
          serviceMap[service.name] = service.key;
        });
        setBaseUrls(serviceMap);
        
        if (selectedService.name && serviceMap[selectedService.name]) {
          // 保持当前服务
        } else if (services.length > 0) {
          const firstService = services[0];
          setSelectedService({ name: firstService.name, url: firstService.key });
        }
        setServicesLoaded(true);
      } catch (error) {
        console.error('Failed to fetch services:', error);
        const defaultServices = {
          "全模型系列Key查询": "全模型系列Key查询",
          "GPT系列Key查询": "GPT系列Key查询"
        };
        setBaseUrls(defaultServices);
        setServicesLoaded(true);
      }
    };
    
    fetchServices();
  }, [selectedService, setSelectedService]);

  // 查询函数
  const handleSearch = async () => {
    if (apikey === '') {
      toast({
        title: "请输入令牌",
        description: "请先输入您的API令牌",
        variant: "destructive"
      });
      return;
    }

    if (!/^sk-[a-zA-Z0-9]{48}$/.test(apikey)) {
      toast({
        title: "令牌格式错误",
        description: "请输入正确格式的令牌（sk-xxxxxxxx）",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      await performQuery();
      
      toast({
        title: "查询成功",
        description: "数据获取完成，正在跳转...",
        variant: "success"
      });
      
      setTimeout(() => {
        setLoading(false);
        navigate('/search');
      }, 800);
    } catch (error) {
      setLoading(false);
      toast({
        title: "查询失败",
        description: error.message || "查询过程中发生错误",
        variant: "destructive"
      });
    }
  };

  const handleSavedKeySearch = async () => {
    setLoading(true);
    
    try {
      await performSavedQuery();
      
      toast({
        title: "查询成功",
        description: "数据获取完成，正在跳转...",
        variant: "success"
      });
      
      setTimeout(() => {
        setLoading(false);
        navigate('/search');
      }, 800);
    } catch (error) {
      setLoading(false);
      toast({
        title: "查询失败",
        description: error.message || "查询过程中发生错误",
        variant: "destructive"
      });
    }
  };

  // 处理多密钥查询
  const handleMultiKeySearch = async (keyId) => {
    setLoading(true);
    
    try {
      await performQueryWithKey(keyId);
      
      toast({
        title: "查询成功",
        description: "数据获取完成，正在跳转...",
        variant: "success"
      });
      
      setTimeout(() => {
        setLoading(false);
        navigate('/search');
      }, 800);
    } catch (error) {
      setLoading(false);
      toast({
        title: "查询失败",
        description: error.message || "查询过程中发生错误",
        variant: "destructive"
      });
    }
  };

  // 处理服务切换
  const handleServiceSwitch = () => {
    const services = Object.keys(baseUrls);
    const currentIndex = services.indexOf(selectedService.name);
    const nextIndex = (currentIndex + 1) % services.length;
    const nextService = services[nextIndex];
    
    setSelectedService({ 
      name: nextService, 
      url: baseUrls[nextService] 
    });
    
    toast({
      title: "服务已切换",
      description: `当前服务: ${nextService.replace('Key查询', '')}`,
      variant: "success"
    });
  };

  // Bento Grid配置 - 保持桌面端原有布局，只在移动端响应式调整
  
  // 重构features以确保组件数量和位置始终一致，避免钩子数量不一致
  const features = [
    // 位置1: API Key查询功能 - 桌面端保持原有布局
    {
      ...ApiKeyQueryCard({
        apikey,
        setAPIKey,
        handleSearch,
        loading,
        greetingText // 传递问候语
      }),
      name: "ApiKeyQuery",
      className: hasSaved 
        ? "col-span-2 row-span-1 group relative overflow-hidden"
        : "col-span-2 row-span-2 group relative overflow-hidden"
    },

    // 位置2: 始终渲染SavedApiKeyCard，让组件内部处理空状态
    {
      ...SavedApiKeyCard({
        loading,
        forceRender: true // 强制渲染，即使没有保存的Key
      }),
      name: "SavedApiKey",
      className: "col-span-1 row-span-1 group relative overflow-hidden"
    },

    // 位置3: 服务状态卡片 - 当没有保存Key时，调整位置
    {
      ...ServiceStatusCard({
        selectedService,
        handleServiceSwitch
      }),
      name: "ServiceStatus",
      className: hasSaved 
        ? "col-span-1 row-span-1 group relative overflow-hidden"  // 有Key时在底部左侧
        : "col-span-1 row-span-2 group relative overflow-hidden"  // 没有Key时占据右侧整列
    },

    // 位置4: 支持的模型卡片
    {
      ...SupportedModelsCard({
        hasSaved: hasSaved, // 传递hasSaved状态
        className: hasSaved 
          ? "col-span-2 row-span-1 group relative overflow-hidden"
          : "col-span-2 row-span-1 group relative overflow-hidden"
      })
    }
  ];

  return (
    <motion.div 
      className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 overflow-hidden flex items-center justify-center p-4 sm:p-6"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      {/* 主内容容器 */}
      <div className="relative z-10 w-full max-w-6xl mx-auto">
        {/* 响应式 Bento Grid */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <ErrorBoundary fallbackMessage="网格布局加载失败，请刷新页面">
            <ResponsiveBentoGrid features={features} hasSaved={hasSaved} />
          </ErrorBoundary>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Home; 